import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { CustomTaxOthersAccordion } from "components/TaxOthers/CustomAccordionComponents";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { CaretDown } from "components/icons/CaretDown";
import TaskTabBar from "components/tasks/taskTabBar";
import { NO_TAG, NO_TAG_KEY } from "constants/tagColors";
import { ALL, ALL_BANK_CONNECTED, ANY_BANK_DISCONNECTED } from "constants/task";
import { useFilters } from "hooks/useFilter";
import authContext from "jwt_context&axios/authContext";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { RefObject, useContext, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllBooksEntityQuery } from "store/apis/bookkeepingTracker";
import { useGetTaskTagQuery } from "store/apis/taskTags";
import {
  BookkeepingTaskQueryParams,
  EntityWithTask,
} from "types/Models/bookkeepingTracker";
import { debounce } from "utils/debouncing";
import { BookkeepingTrackerFilter } from "./BookkeepingTrackerFilter";
import { BookkeepingTrackerSlider } from "./BookkeepingTrackerSlider";
import { BookkeepingTrackerTags } from "./BookkeepingTrackerTags";
import { EntityTaskStatusSummary } from "./EntityTaskStatusSummary";
import { EntityTaskTable } from "./EntityTaskTable";
import { GroupNote } from "./GroupNote";
import { StatusTab } from "./StatusTab";
import { useSearchCompanyGroupInvoicesQuery } from "store/apis/billingInvoice";
import { DraftInvoices } from "components/billing/DraftInvoices";
import { Badge } from "components/design/badge";
import ToolTip from "components/design/toolTip";
import {
  CRM_SUBSCRIPTION_BADGE_MAP,
  CRM_SUBSCRIPTION_TYPE,
  CrmSubscriptionType,
} from "dictionaries";

export type BookkeepingTrackerFilterValues = {
  page: number;
  search_term: string | null;
  pending_actions_view: string | null;
  task_tag_filter_uuids: string | null;
  company_tag_filter_uuids: string | null;
  company_groups: string | null;
  bank_connections: string | null;
  service_task_filter: string | null;
  season_filter_uuids: string | null;
  service_team_uuids: string | null;
  team_member_uuids: string | null;
  status: string;
  show_unassigned_team: string | null;
  show_unassigned_bookkeeper: string | null;
  formation_type_filter_uuids: string | null;
  task_sort_by: string | null;
  accountant_access: string | null;
  spoc_ids: string | null;
  books_global_status: string | null;
  default_open_row: string;
};

const INITIAL_FILTER_VALUES: BookkeepingTrackerFilterValues = {
  page: 1,
  search_term: null,
  pending_actions_view: null,
  task_tag_filter_uuids: null,
  company_tag_filter_uuids: null,
  company_groups: null,
  bank_connections: null,
  service_task_filter: null,
  season_filter_uuids: null,
  service_team_uuids: null,
  team_member_uuids: null,
  status: ALL,
  show_unassigned_team: null,
  show_unassigned_bookkeeper: null,
  formation_type_filter_uuids: null,
  task_sort_by: null,
  accountant_access: null,
  spoc_ids: null,
  books_global_status: null,
  default_open_row: "",
};

const EMPTY_ENTITIES: EntityWithTask[] = [];

type TableMetaType = {
  filtersRef: RefObject<BookkeepingTaskQueryParams>;
};

const BankAccountsCell = ({ row }: { row: EntityWithTask }) => {
  const { connection_details, uuid: entityId, group } = row;

  const connectedCount = connection_details.filter(
    (c) => c.connection_available && !c.is_all_disconnected
  ).length;
  const total = connection_details.length;
  const allDisconnected = total > 0 && connectedCount === 0;
  const visible = connection_details.slice(0, 2);
  const overflow = connection_details.length - visible.length;

  if (connection_details.length === 0) {
    return <div className="t-text-text-30 t-text-body-sm">—</div>;
  }

  return (
    <div className="t-flex t-flex-col t-gap-2 t-items-center">
      <div
        className={classNames("t-text-body-sm t-font-medium", {
          "t-text-red": allDisconnected,
          "t-text-dark_green-70": !allDisconnected && connectedCount === total,
          "t-text-text-60": !allDisconnected && connectedCount !== total,
        })}
      >
        {connectedCount}/{total} connected
      </div>
      <div className="t-flex t-flex-col t-gap-2">
        {visible.map((conn) => {
          const isClosed = conn.is_all_disconnected;
          const isDisconnected =
            !conn.connection_available && !conn.is_all_disconnected;
          const dataSourceUrl = `/books/data-sources/${conn.entity_item_id}?company=${group.uuid}&entity=${entityId}`;

          let statusLabel: string | null = null;
          if (isClosed) {
            statusLabel = "Account closed";
          } else if (isDisconnected) {
            statusLabel = "Account disconnected";
          } else {
            statusLabel = "Account connected";
          }

          const accessLabel = conn.accountant_access
            ? "Accountant access granted"
            : "No accountant access";
          const tooltipText = `${statusLabel} · ${accessLabel}`;

          const pill = (
            <a
              href={dataSourceUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={classNames(
                "t-flex t-w-[130px] t-items-center t-gap-1.5 t-rounded-full t-text-body-sm t-no-underline t-overflow-hidden t-pl-1.5 t-pr-3 t-h-7",
                {
                  "t-bg-dark_green-30 t-text-dark_green-70":
                    !isClosed && !isDisconnected,
                  "t-bg-red-10 t-text-red": isClosed,
                  "t-bg-yellow-20 t-text-yellow-80": isDisconnected,
                }
              )}
            >
              <div className="t-h-5 t-w-5 t-rounded-full t-bg-white t-flex t-items-center t-justify-center t-shrink-0 t-overflow-hidden t-border t-border-solid t-border-neutral-10">
                {conn.logo_url ? (
                  <img
                    src={conn.logo_url}
                    alt={conn.name}
                    className="t-h-full t-w-full t-object-cover"
                  />
                ) : (
                  <span
                    className={classNames("t-text-[9px] t-font-semibold", {
                      "t-text-dark_green-70": !isClosed && !isDisconnected,
                      "t-text-red": isClosed,
                      "t-text-yellow-80": isDisconnected,
                    })}
                  >
                    {conn.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <span className="t-truncate t-flex-1">{conn.name}</span>
              <div className="t-flex t-items-center t-gap-3 t-shrink-0">
                <span className="t-text-[10px] t-opacity-60">
                  {conn.account_count}
                </span>
                <span
                  className={classNames("t-h-2 t-w-2 t-rounded-full", {
                    "t-bg-dark_green-70": conn.accountant_access,
                    "t-bg-red-50": !conn.accountant_access,
                  })}
                />
              </div>
            </a>
          );

          return (
            <ToolTip key={conn.entity_item_id} text={tooltipText} side="top">
              {pill}
            </ToolTip>
          );
        })}
        {overflow > 0 && (
          <span className="t-text-body-sm t-text-text-30">
            +{overflow} more
          </span>
        )}
      </div>
    </div>
  );
};

const FilterTab = ({
  searchTerm,
  onSearch,
}: {
  searchTerm: string | null;
  onSearch: (value: string) => void;
}) => {
  const handleSearch = debounce((e) => {
    const { value }: { value: string } = e.target;
    onSearch(value);
  });
  return (
    <div>
      <Search
        customSize="small"
        placeholder="Search.."
        block
        onChange={handleSearch}
        defaultValue={searchTerm || ""}
      />
    </div>
  );
};

const columnHelper = createColumnHelper<EntityWithTask>();

const columns = [
  columnHelper.accessor("name", {
    size: 220,
    header: "ENTITY AND GROUP",
    cell: (info) => {
      const rowData = info.row.original;

      return (
        <div className="t-flex t-gap-2 t-items-center">
          <div className="t-flex t-gap-6 t-items-center">
            <span className="group-data-state-open:t-rotate-0 group-data-state-closed:-t-rotate-90 t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out">
              <CaretDown />
            </span>
            <div>
              <ConditionalLink
                target="_blank"
                to={`/admin/crm/${rowData.group.uuid}/entity/${rowData.uuid}`}
                className="t-text-body-sm"
              >
                {info.getValue()}
              </ConditionalLink>
              <ConditionalLink
                target="_blank"
                to={`/admin/crm/${rowData.group.uuid}`}
              >
                <div className="t-text-body-sm t-text-text-30 hover:t-text-purple hover:t-underline">
                  {rowData.group.name}{" "}
                </div>
              </ConditionalLink>
            </div>
          </div>
          {Boolean(rowData?.subscription_name) &&
            rowData?.subscription_name !== "NOT_STARTED" && (
              <Badge
                size="small"
                color={
                  CRM_SUBSCRIPTION_TYPE[
                    rowData?.subscription_name as CrmSubscriptionType
                  ]
                }
              >
                {
                  CRM_SUBSCRIPTION_BADGE_MAP[
                    rowData?.subscription_name as CrmSubscriptionType
                  ]
                }
              </Badge>
            )}
        </div>
      );
    },
  }),
  columnHelper.accessor("is_retainer_entity", {
    size: 150,
    header: "RETAINER",
    cell: (info) => {
      const isRetainer = info.getValue();
      if (!isRetainer) return null;
      return (
        <span className="t-inline-flex t-items-center t-rounded t-bg-dark_green-30 t-text-dark_green-70 t-text-subtext-sm t-px-2 t-py-1 t-font-medium t-whitespace-nowrap">
          Retainer
        </span>
      );
    },
  }),
  columnHelper.accessor("retainer_amount", {
    size: 180,
    header: "RETAINER AMT",
    cell: (info) => {
      const amount = info.getValue();
      if (amount == null)
        return <span className="t-text-text-30 t-text-body-sm">—</span>;
      return (
        <span className="t-text-body-sm t-font-medium">
          ${amount.toLocaleString()}
        </span>
      );
    },
  }),
  columnHelper.accessor("spoc", {
    size: 220,
    header: "SPOC",
    cell: (info) => {
      const spoc = info.getValue();
      if (!spoc)
        return <span className="t-text-text-30 t-text-body-sm">—</span>;
      return <span className="t-text-body-sm">{spoc.name}</span>;
    },
  }),
  columnHelper.accessor("books_preparer", {
    size: 220,
    header: "BOOKS PREPARER",
    cell: (info) => {
      const preparer = info.getValue();
      if (!preparer)
        return <span className="t-text-text-30 t-text-body-sm">—</span>;
      return <span className="t-text-body-sm">{preparer.name}</span>;
    },
  }),
  columnHelper.accessor("group.tags", {
    size: 240,
    header: "GROUP TAGS",
    cell: ({ row }) => {
      return <BookkeepingTrackerTags group={row.original.group} />;
    },
  }),
  columnHelper.display({
    id: "task_status_summary",
    size: 280,
    header: "TASK STATUS",
    cell: (info: CellContext<EntityWithTask, unknown>) => {
      const { filtersRef } = info.table.options.meta as TableMetaType;
      return (
        <EntityTaskStatusSummary
          entityId={info.row.original.uuid}
          filtersRef={filtersRef}
        />
      );
    },
  }),

  columnHelper.accessor("total_bank_connections", {
    size: 220,
    header: () => <div className="t-text-center">BANK ACCOUNTS</div>,
    cell: (info) => <BankAccountsCell row={info.row.original} />,
  }),

  columnHelper.accessor("has_open_items", {
    size: 130,
    header: () => <div className="t-text-center">OPEN ITEMS</div>,
    cell: (info) => {
      const hasOpenItems = info.getValue();
      return (
        <div className="t-flex t-justify-center">
          {hasOpenItems ? (
            <ToolTip text="Has pending open items awaiting response" side="top">
              <span className="t-inline-flex t-items-center t-gap-1.5 t-rounded t-bg-yellow-20 t-text-yellow-80 t-text-subtext-sm t-px-2 t-py-1 t-font-medium t-cursor-default t-whitespace-nowrap">
                <span className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-yellow-80 t-shrink-0" />
                Pending
              </span>
            </ToolTip>
          ) : (
            <span className="t-text-text-30 t-text-body-sm">—</span>
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor("group.uuid", {
    header: "",
    size: 60,
    cell: ({ row }) => {
      return (
        <div
          className="t-flex t-justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <GroupNote
            groupId={row.original.group.uuid}
            currentNoteLabel="Notes for this task"
            allNotesCount={row.original.notes_count}
          />
        </div>
      );
    },
  }),
];

export const BookkeepingTracker = () => {
  const { practicecrmId } = useParams<{ practicecrmId?: string }>();
  const { authtoken } = useContext(authContext);
  const serviceTeamId = practicecrmId || authtoken.service_team_id;

  const { values, updateFilter, updateMultipleFilters } =
    useFilters<BookkeepingTrackerFilterValues>({
      initialValue: INITIAL_FILTER_VALUES,
      useQueryParams: true,
    });

  const pageNumber = Number(values.page);
  const pendingActionsView = values.pending_actions_view;

  const { data: tagsData = [] } = useGetTaskTagQuery();

  const filters = useMemo(
    () => ({
      search_term: values.search_term,
      page_num: pageNumber,
      task_tag_filter_uuids: values.task_tag_filter_uuids,
      company_tag_filter_uuids: values.company_tag_filter_uuids,
      task_sort_by: values.task_sort_by || null,
      task_list_type: values.status === ALL ? null : values.status,
      show_unassigned_preparer:
        Boolean(values.show_unassigned_bookkeeper) || null,
      show_unassigned_team: Boolean(values.show_unassigned_team) || null,
      service_team_ids: values.service_team_uuids,
      preparer_ids: values.team_member_uuids,
      formation_types: values.formation_type_filter_uuids,
      service: values.service_task_filter,
      seasons: values.season_filter_uuids,
      is_all_bank_connected:
        values.bank_connections === ALL_BANK_CONNECTED || null,
      is_any_bank_disconnected:
        values.bank_connections === ANY_BANK_DISCONNECTED || null,
      accountant_access_status: values.accountant_access || null,
      books_global_status: values.books_global_status || null,
    }),
    [values, pageNumber]
  );

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const { data, isLoading, isFetching, isSuccess } = useGetAllBooksEntityQuery(
    filters,
    {
      skip: Boolean(pendingActionsView),
    }
  );

  const { data: invoices, isFetching: isFetchingInvoices } =
    useSearchCompanyGroupInvoicesQuery(
      {
        search_term: values.search_term || "",
        page_num: pageNumber,
        company_groups_ids: decodeURIComponent(
          values.company_groups || ""
        ).split(","),
        service: values.service_task_filter
          ? values.service_task_filter
          : undefined,
        spoc_ids: values.spoc_ids ? values.spoc_ids : undefined,
      },
      { skip: !Boolean(pendingActionsView) }
    );

  const {
    entities = EMPTY_ENTITIES,
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  const tags = useMemo(
    () => [
      ...tagsData,
      {
        title: NO_TAG,
        uuid: NO_TAG_KEY,
        bg_color_hex: "",
        color_hex: "",
        tag_category: 0,
      },
    ],
    [tagsData]
  );

  const isPendingActionsView = Boolean(pendingActionsView);
  const paginationData = isPendingActionsView
    ? {
        page_num: pageNumber,
        per_page: invoices?.per_page || 1,
        total_count: invoices?.total_count || 0,
        total_pages: invoices?.total_pages || 1,
      }
    : {
        page_num: pageNumber,
        per_page,
        total_count,
        total_pages,
      };

  const isEmpty = entities.length === 0;

  const tableData = useMemo(() => entities, [entities]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.uuid,
    meta: {
      filtersRef,
    } satisfies TableMetaType,
  });

  const handleSearch = (value: string) => {
    updateFilter("search_term", value);
  };

  const handleStatusChange = (value: string) => {
    updateMultipleFilters({ status: value, page: 1 });
  };

  const handlePageNumChange = (page: number) => {
    updateFilter("page", page);
  };

  return (
    <DashboardContainer className="t-h-full">
      <DashboardContainer.Header>
        <TaskTabBar defaultValue="admin/bookkeeping-tracker" />
      </DashboardContainer.Header>
      <div className="t-pb-4 t-px-5 t-flex t-flex-col t-gap-4 t-h-full t-overflow-hidden">
        <div className="t-flex t-justify-between t-items-center">
          <div className="t-flex t-gap-4 t-w-2/3">
            <StatusTab
              pendingInvoices={invoices?.draft_invoices_count}
              status={values.status}
              setStatus={handleStatusChange}
            />
            <FilterTab
              searchTerm={values.search_term}
              onSearch={handleSearch}
            />
          </div>
          <PaginationHandler
            {...paginationData}
            onPageNumChange={handlePageNumChange}
            isLoading={isFetching || isFetchingInvoices}
          />
        </div>
        <BookkeepingTrackerFilter
          companyGroups={values.company_groups}
          selectedTag={values.task_tag_filter_uuids}
          filters={JSON.stringify(filters)}
          selectedCompanyTag={values.company_tag_filter_uuids}
          tags={tags}
          selectedServicesFilter={values.service_task_filter}
          taskStates={[]}
          selectedSeason={values.season_filter_uuids}
          selectedSort={values.task_sort_by}
          showSortFeature={true}
          selectedTeams={values.service_team_uuids}
          selectedMembers={values.team_member_uuids}
          serviceTeamId={serviceTeamId}
          showUnassignedTeam={values.show_unassigned_team}
          showUnassignedBookkeeper={values.show_unassigned_bookkeeper}
          selectedBankConnection={values.bank_connections}
          selectedFormationType={values.formation_type_filter_uuids}
          selectedAccountAccess={values.accountant_access}
          pendingActionsView={pendingActionsView}
          selectedSpocIds={values.spoc_ids}
          selectedBooksGlobalStatus={values.books_global_status}
          updateFilter={updateFilter}
          updateMultipleFilters={updateMultipleFilters}
        />

        {isPendingActionsView && invoices?.invoices ? (
          <div className="t-h-full t-pb-20">
            <DraftInvoices showCompanyInfo invoices={invoices?.invoices} />
          </div>
        ) : (
          <div className="t-overflow-auto t-flex-1 t-min-h-0">
            <div style={{ minWidth: 1920 }}>
              <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
                <Async.Empty>
                  <div className="t-flex t-items-center t-h-80">
                    <EmptyScreen
                      text="No Bookkeeping found"
                      className="!t-h-full"
                    />
                  </div>
                </Async.Empty>
                <Async.Success>
                  <div className="t-flex t-bg-neutral-10 t-border-b t-border-solid t-border-neutral-0 t-sticky t-top-0 t-z-20">
                    {table
                      .getHeaderGroups()[0]
                      ?.headers.map((header, index) => {
                        const isFirst = index === 0;
                        const size = header.getSize();
                        return (
                          <div
                            key={header.id}
                            className={classNames(
                              "t-px-3 t-py-4 t-uppercase t-text-subtext-sm t-text-text-30 t-flex t-items-center t-justify-center",
                              {
                                "t-sticky t-left-0 t-z-[21] t-bg-neutral-10 t-drop-shadow-table-left-pin !t-justify-start":
                                  isFirst,
                              }
                            )}
                            style={{
                              width: size,
                              minWidth: size,
                              flexShrink: 0,
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </div>
                        );
                      })}
                  </div>
                  <CustomTaxOthersAccordion.Root
                    type="single"
                    collapsible
                    defaultValue={values.default_open_row}
                  >
                    {table.getRowModel().rows.map((row) => (
                      <CustomTaxOthersAccordion.Item
                        key={row.id}
                        value={row.id}
                        propClass="!t-pb-0"
                      >
                        <CustomTaxOthersAccordion.Trigger className="t-group data-[state=open]:t-border data-[state=open]:t-border-solid data-[state=open]:t-border-l-0 data-[state=open]:t-border-t-0 data-[state=open]:t-border-r-0 data-[state=open]:t-border-b-neutral-10">
                          <div className="t-group t-flex t-items-center t-text-body-sm t-cursor-pointer hover:t-bg-surface-lighter-grey">
                            {row.getVisibleCells().map((cell, index) => {
                              const isFirst = index === 0;
                              const size = cell.column.getSize();
                              return (
                                <div
                                  key={cell.id}
                                  className={classNames(
                                    "t-px-3 t-py-3 t-flex t-items-center t-justify-center",
                                    {
                                      "t-sticky t-left-0 t-z-10 t-bg-white group-hover:t-bg-surface-lighter-grey t-drop-shadow-table-left-pin !t-justify-start [height:stretch]":
                                        isFirst,
                                    }
                                  )}
                                  style={{
                                    width: size,
                                    minWidth: size,
                                    flexShrink: 0,
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CustomTaxOthersAccordion.Trigger>
                        <CustomTaxOthersAccordion.Content className="t-bg-surface-lighter-grey t-w-full">
                          <EntityTaskTable
                            entityId={row.original.uuid}
                            group={row.original.group}
                            bookkeepingSoftware={
                              row.original.bookkeeping_software
                            }
                            connectionDetails={row.original.connection_details}
                            filters={filters}
                          />
                        </CustomTaxOthersAccordion.Content>
                      </CustomTaxOthersAccordion.Item>
                    ))}
                  </CustomTaxOthersAccordion.Root>
                </Async.Success>
              </Async.Root>
            </div>
          </div>
        )}
      </div>
      <BookkeepingTrackerSlider status={values.status} />
    </DashboardContainer>
  );
};
